import { FormEvent, useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u39jn8l97n3eas3ghg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, String(value)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(
          String(serverMsg).includes('spam')
            ? '提交失败，请稍后重试。'
            : String(serverMsg) || '提交失败，请稍后重试。'
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <SchemaOrg
        type="Organization"
        data={{
          name: '在线麻将牌',
          url: 'https://tileblast.github.io',
          description: '免费在线麻将游戏平台，致力于为全球麻将爱好者提供最正宗的在线麻将体验。',
          address: {
            '@type': 'PostalAddress',
            addressLocality: '北京',
            addressCountry: 'CN',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+86-400-000-0000',
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
          sameAs: [
            'https://weibo.com/mahjong-online',
            'https://weixin.qq.com/mahjong-online',
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 在线麻将牌',
          description: '了解在线麻将牌的品牌故事、团队信誉和联系方式。我们致力于打造最好的免费在线麻将平台。',
          url: 'https://tileblast.github.io/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '在线麻将牌',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tileblast.github.io' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20and%20inviting%20game%20development%20office%20with%20mahjong%20elements%2C%20modern%20Chinese%20tech%20company%20interior%2C%20creative%20workspace%2C%20soft%20natural%20lighting%2C%20team%20collaboration%20atmosphere%2C%20professional%20photography%2C%20clean%20minimalist%20design&width=1920&height=760&seq=about-hero&orientation=landscape"
            alt="关于在线麻将牌"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
              关于<strong className="text-accent-400">在线麻将牌</strong>
            </h1>
            <p className="text-white/80 max-w-xl text-sm md:text-base">
              了解我们的品牌故事、信誉承诺与团队使命
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <p className="text-foreground-600">
                传承麻将文化，连接全球玩家
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  在线麻将牌成立于2020年，由一群热爱麻将文化的游戏开发者和资深玩家共同创建。我们的使命是通过互联网技术，让全球麻将爱好者随时随地享受最正宗的麻将游戏体验。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  作为国内领先的免费在线麻将平台，我们始终坚持&ldquo;品质为王、用户至上&rdquo;的理念。我们的团队深入研究各地麻将规则，精心打磨每一款玩法的细节，力求还原线下麻将的真实感受。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  从国标麻将的严谨规范，到四川麻将的热血刺激，再到广东麻将的轻松惬意——在线麻将牌汇聚了中国麻将文化的精华，让您在一屏之间，尽享四方桌的乐趣。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20office%20interior%20with%20mahjong%20themed%20decorations%2C%20warm%20cozy%20atmosphere%2C%20creative%20team%20workspace%2C%20green%20plants%2C%20wooden%20furniture%2C%20soft%20natural%20light%2C%20professional%20photography%2C%20inviting%20environment&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="在线麻将牌团队"
                  className="w-full h-auto object-cover"
                  title="在线麻将牌团队"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '500万+', label: '注册玩家' },
                { value: '6种', label: '麻将玩法' },
                { value: '99.9%', label: '服务可用率' },
                { value: '4.9分', label: '用户评分' },
              ].map((stat, idx) => (
                <div key={idx} className="p-6">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                信誉承诺
              </h2>
              <p className="text-foreground-600">
                我们以诚信为本，用心服务每一位玩家
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '公平游戏保障',
                  desc: '采用国际认证的随机洗牌算法，确保每局游戏绝对公平。系统自动监控异常行为，维护健康游戏环境。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '数据安全保护',
                  desc: '严格遵守数据保护法规，采用银行级加密技术保护用户隐私，您的个人信息安全有保障。',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '7x24小时客服',
                  desc: '专业客服团队全天候在线，快速响应您的问题和反馈，确保最佳游戏体验。',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '持续更新优化',
                  desc: '每周更新维护，持续优化游戏性能，定期推出新玩法和活动，保持游戏新鲜感。',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 p-5 bg-background-50 border border-background-200/70 rounded-xl"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <p className="text-foreground-600">
                有任何问题或建议？请随时与我们联系，我们会尽快回复您
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <div className="contact-honeypot">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>

              {formStatus === 'success' && (
                <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700 text-center">
                  <i className="ri-check-line mr-1"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-center">
              <div className="p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-600">support@mahjong-online.com</p>
              </div>
              <div className="p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">客服电话</h3>
                <p className="text-sm text-foreground-600">400-000-0000</p>
              </div>
              <div className="p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">服务时间</h3>
                <p className="text-sm text-foreground-600">每日 9:00 - 22:00</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}